const GA4_SCRIPT_PREFIX = 'ga4-script-';
const GTM_SCRIPT_ID = 'gtm-script';

/**
 * Define `dataLayer` and the `gtag` queue function if the page has neither, so
 * commands can be issued before gtag.js arrives.
 */
export function ensureGtag(): void {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments as unknown as Record<string, unknown>);
    };
    window.gtag('js', new Date());
  }
}

/**
 * Load Google Analytics 4 (gtag.js).
 * Injects the gtag script and configures it with the measurement ID.
 * Uses element ID guard to prevent duplicate loads.
 */
export function loadGA4(measurementId: string, sendPageView: boolean = false): void {
  const scriptId = `${GA4_SCRIPT_PREFIX}${measurementId}`;
  if (document.getElementById(scriptId)) return;

  ensureGtag();

  const script = document.createElement('script');
  script.id = scriptId;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag!('config', measurementId, {
    send_page_view: sendPageView,
    client: 'v2',
  });
}

/**
 * Load Google Tag Manager.
 * Injects the standard GTM snippet.
 */
export function loadGTM(containerId: string): void {
  if (document.getElementById(GTM_SCRIPT_ID)) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

/**
 * Load Facebook Pixel (fbevents.js).
 * Injects the pixel snippet and fires the initial PageView.
 */
export function loadFacebookPixel(pixelId: string): void {
  if (window.fbq) return;

  // Standard Facebook Pixel snippet (minified version)
  const n = (window.fbq = function (...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    (n as unknown as { callMethod?: (...a: unknown[]) => void }).callMethod
      ? (n as unknown as { callMethod: (...a: unknown[]) => void }).callMethod(...args)
      : (n as unknown as { queue: unknown[] }).queue.push(args);
  });
  (n as unknown as { push: typeof n }).push = n;
  (n as unknown as { loaded: boolean }).loaded = true;
  (n as unknown as { version: string }).version = '2.0';
  (n as unknown as { queue: unknown[] }).queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}
